"use client";

import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { CheckCircle, Globe, HeartHandshake, Briefcase } from "lucide-react";
import { Container } from "@/components/ui/container";
import { SectionContainer } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { TextGradient } from "@/components/ui/text-gradient";

export function MissionSection() {
  const { ref: missionRef, inView: missionInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const { ref: valuesRef, inView: valuesInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <SectionContainer id="mission" className="bg-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 opacity-10 pattern-bg"></div>
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 opacity-10 dot-pattern"></div>
      
      <SectionHeading
        title={
          <>
            Our <TextGradient text="Mission" /> & Values
          </>
        }
        subtitle="We're committed to creating a world where blue Lego bricks receive the respect, representation, and recognition they deserve."
      />

      {/* Mission & Vision */}
      <div
        ref={missionRef}
        className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-20"
      >
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={missionInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="p-1.5 mb-6 w-fit rounded-lg bg-gradient-to-r from-blue-700 to-blue-500">
            <div className="bg-white rounded-md px-4 py-2">
              <span className="text-blue-medium font-semibold">Our Mission</span>
            </div>
          </div>
          <h3 className="text-2xl md:text-3xl font-heading font-bold mb-6">
            To ensure blue Lego bricks are valued equally in all building projects
          </h3>
          <p className="text-muted-foreground mb-6">
            Blue Legos Matter advocates for the equal treatment and representation of blue Lego pieces in playsets, displays, and creative builds worldwide. We believe that blue bricks contribute essential aesthetic and structural value that has been historically underappreciated.
          </p>
          <ul className="space-y-3 mb-8">
            {['Increase blue brick representation in official Lego sets', 
              'Educate builders about the versatility of blue pieces', 
              'Rescue and redistribute abandoned blue bricks'].map((item, i) => (
              <li key={i} className="flex items-start">
                <CheckCircle className="h-5 w-5 text-blue-medium shrink-0 mt-0.5 mr-3" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <Button variant="blue-solid" isAnimated>Learn About Our Work</Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={missionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700 transform rotate-3 rounded-2xl"></div>
            <div className="relative bg-white p-8 rounded-2xl border border-gray-100 shadow-xl transform -rotate-3">
              <div className="p-1.5 mb-6 w-fit rounded-lg bg-blue-light">
                <div className="px-4 py-1.5">
                  <span className="text-blue-dark font-semibold">Vision Statement</span>
                </div>
              </div>
              <h3 className="text-xl md:text-2xl font-heading font-bold mb-4">
                A world where blue bricks have equal opportunity to be part of amazing creations
              </h3>
              <p className="text-muted-foreground">
                We envision a building community where color bias is eliminated and all bricks are judged by their creative potential, not their hue. Blue bricks should never be left at the bottom of the bin or used as mere background elements.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex -space-x-4">
                  {[...Array(4)].map((_, i) => (
                    <div 
                      key={i} 
                      className="w-10 h-10 rounded-full border-2 border-white overflow-hidden"
                      style={{ 
                        backgroundColor: ['#0057B7', '#205CA4', '#3B82F6', '#93C5FD'][i],
                        zIndex: 4-i
                      }}
                    />
                  ))}
                </div>
                <div className="text-sm">
                  <p className="font-medium">Join 5,000+ supporters</p>
                  <p className="text-muted-foreground">Building a better world for blue bricks</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Core Values */}
      <div ref={valuesRef}>
        <h3 className="text-2xl md:text-3xl font-heading font-semibold text-center mb-12">
          Our Core Values
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: <Globe className="h-6 w-6" />,
              title: "Brick Inclusivity",
              description:
                "We believe that every Lego color deserves equal representation in builds and collections, with special focus on the often overlooked blue pieces."
            },
            {
              icon: <HeartHandshake className="h-6 w-6" />,
              title: "Creative Advocacy",
              description:
                "We use creative approaches to highlight the versatility and beauty of blue bricks, showing their potential in various building contexts."
            },
            {
              icon: <Briefcase className="h-6 w-6" />,
              title: "Educational Outreach",
              description:
                "We educate builders of all ages about effective integration of blue bricks, promoting color diversity in Lego creations worldwide."
            },
          ].map((value, i) => (
            <motion.div
              key={i}
              className={cn(
                "bg-white rounded-xl p-6 shadow-sm border border-gray-100",
                "hover:border-blue-200 hover:shadow-md transition-all duration-300"
              )}
              initial={{ opacity: 0, y: 30 }}
              animate={valuesInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: i * 0.2 }}
            >
              <div className="w-12 h-12 rounded-full bg-blue-light flex items-center justify-center text-blue-medium mb-5">
                {value.icon}
              </div>
              <h4 className="font-heading text-xl font-semibold mb-3">{value.title}</h4>
              <p className="text-muted-foreground">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </SectionContainer>
  );
}